import React, { useState, useEffect, useRef } from 'react';
import Header from './Header';
import Sidebar from './Sidebar';
import {axiosInstance} from './helper/axiosConfig';
import './CreateAppoinment.css';
import './Dashboard.css';
import { faArrowLeft, faPlus, faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import { specialities } from '../data/specialities';
import flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import toast, { Toaster } from 'react-hot-toast';
import { isAdmin, isDoctor } from './helper/authRoles';

const CreateAppointment = () => {
    const [formData, setFormData] = useState({
        date: '',
        time: '',
        speaciality: '',
        doctor: '',
        patient_name: '',
        patient_number: ''
    });
    const [doctors, setDoctors] = useState([]);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [loading, setLoading] = useState(false);
    const [patients, setPatients] = useState([]);
    const [patientNotFound, setPatientNotFound] = useState(false);
    const [schedule, setSchedule] = useState([]);
    const [availableDays, setAvailableDays] = useState([]);
    const [availableTimes, setAvailableTimes] = useState([]);
    const [timeRange, setTimeRange] = useState({ minTime: null, maxTime: null });
    const history = useNavigate();

    const datePickerRef = useRef(null);
    const timePickerRef = useRef(null);

    useEffect(() => {
      if(!isAdmin()) {
        history('/dashboard'); // Redirect after successful creation
        return;
      }

        if (datePickerRef.current) {
            flatpickr(datePickerRef.current, {
                dateFormat: 'Y-m-d',
                minDate: 'today',
                disable: [
                    (date) => !availableDays.includes(date.toLocaleString('en-US', { weekday: 'long'})),
                ],
                onChange: handleDateChange,
            });
        }
    }, [availableDays]);


    useEffect(() => {
        if (timePickerRef.current) {
            flatpickr(timePickerRef.current, {
                enableTime: true,
                noCalendar: true,
                dateFormat: 'H:i K',
                time_24hr: false,
                disableMobile: true,
                // defaultDate: timeRange.minTime,
                minTime: timeRange.minTime,
                maxTime: timeRange.maxTime,
                onChange: handleTimeChange,
            });
        }
    }, [timeRange]);

    const fetchDoctors = async (selectedSpeciality = '') => {
        try {
            const apiUrl = selectedSpeciality
                ? `${process.env.REACT_APP_AXIOS_BASE_URL}/api/doctor-all?speciality=${selectedSpeciality}`
                : `${process.env.REACT_APP_AXIOS_BASE_URL}/api/doctor-all`;
            const response = await axiosInstance.get(apiUrl);
            setDoctors(response.data.results);
        } catch (error) {
            console.error('Error fetching doctors:', error);
        }
    };

    useEffect(() => {
        fetchDoctors(formData.speciality);
    }, [formData.speciality]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        if (name === 'patient_number') {
            fetchPatientsByNumber(value);
        }
        if (name === 'speaciality') {
            fetchDoctors(value);
        }
        setLoading(false);
    };

    const convertTo24HourFormat = (timeString) =>  {
      const [time, period] = timeString.split(' ');
      const [hour, minute] = time.split(':');
      let formattedHour = parseInt(hour);

      if (period === 'PM' && formattedHour !== 12) {
        formattedHour += 12;
      } else if (period === 'AM' && formattedHour === 12) {
        formattedHour = 0;
      }
      return `${formattedHour.toString().padStart(2, '0')}:${minute}`;  

    }


    const fetchPatientsByNumber = async (mobileNumber) => {
        if (mobileNumber.length >= 10) {
            try {
                const response = await axiosInstance.get(`${process.env.REACT_APP_AXIOS_BASE_URL}/api/patient_by_mobile/${mobileNumber}`);
                if (response.data.status === 1 && response.data.data.length > 0) {
                    setPatients(response.data.data);
                    setPatientNotFound(false);
                } else {
                    setPatients([]);
                    setPatientNotFound(true);
                }
            } catch (error) {
                console.error('Error fetching patients:', error);
                setPatientNotFound(true);
            }
        } else {
            setPatients([]);
            setPatientNotFound(false);
        }
    };

    const handleDoctorChange = async (e) => {
        const doctorId = e.target.value;
        setFormData({ ...formData, doctor: doctorId, date: '', time: '' });

        if (doctorId) {
            try {
                const response = await axiosInstance.get(`/api/schedule-doctor/${doctorId}`);
                const doctorSchedule = response.data;

                if (doctorSchedule.length > 0) {
                    setSchedule(doctorSchedule);
                    setAvailableDays(doctorSchedule.map(schedule => schedule.day));
                    setAvailableTimes([]);
                } else {
                    setSchedule([]);
                    setAvailableDays([]);
                    setAvailableTimes([]);
                }
            } catch (error) {
                console.error('Error fetching doctor schedule:', error);
                setSchedule([]);
                setAvailableDays([]);
                setAvailableTimes([]);
            }
        }
    };

    const handleDateChange = (selectedDates) => {
        if (selectedDates.length > 0) {
            const selectedDate = selectedDates[0];

            // Convert selectedDate to the correct weekday without forcing UTC
            const selectedDay = selectedDate.toLocaleString('en-US', { weekday: 'long' });

            console.log("The selected day is: ", selectedDay);

            const validTimes = schedule.find(schedule => schedule.day === selectedDay);

            if (validTimes) {
                setTimeRange({
                    minTime: validTimes.from_time.slice(0, 5),
                    maxTime: validTimes.to_time.slice(0, 5),
                });
            } else {
                setTimeRange({ minTime: null, maxTime: null });
            }

            // Convert date to "Y-m-d" format manually to avoid timezone shifts
            const formattedDate = `${selectedDate.getFullYear()}-${(selectedDate.getMonth() + 1).toString().padStart(2, '0')}-${selectedDate.getDate().toString().padStart(2, '0')}`;

            setFormData({ ...formData, date: formattedDate });
        }
    };

    const handleTimeChange = (selectedTimes) => {
        const selectedTime = selectedTimes[0];
        const formattedTime = selectedTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true });
        setFormData({ ...formData, time: formattedTime });
    };
    // const handleTimeChange = (selectedTimes) => {
    //     const selectedTime = selectedTimes[0];
    //     setFormData({ ...formData, time: selectedTime.toTimeString().slice(0, 5) });
    // };
    const handleSubmit = async (e) => {
        e.preventDefault();
        // setLoading(true);
        // Add validation
        let hasError = false;
        const errors = {};

        if (!formData.speaciality) {
          toast.error("Speciality is required.")
          return;
        }
        if (!formData.doctor) {
          toast.error('Doctor is required.');
          return;
        }
        if (!formData.date) {
          toast.error('Doctor is required.');
          return;
        }
        if (!formData.time) {
          toast.error('Time is required.');
          return;
        }
        if (!formData.patient_number) {
          toast.error('Patient number status is required.');
          return;
        }
        if (!formData.patient_name) {
          toast.error('Patient name is required.');
          return;
        }


        // if (!formData.date) {
        //   errors.date = 'Date is required';
        //   hasError = true;
        // }
        // if (!formData.time) {
        //   errors.time = 'Time is required';
        //   hasError = true;
        // }

        if (hasError) {
          setErrors(errors);
          // setLoading(false);
          return; // Prevent form submission if there are errors
        }

        // Convert time to 24-hour format
        const formattedTime = convertTo24HourFormat(formData.time);

        // Optional: Adjust time zone if necessary (replace with your time zone adjustment logic)
        // const adjustedTime = adjustTimeZone(formattedTime);
        const submissionData = {
            ...formData,
            time: formattedTime,
        };
        try {

            await toast.promise(
                axiosInstance.post(`${process.env.REACT_APP_AXIOS_BASE_URL}/api/appoinment/`, submissionData),
                {
                  loading: 'Creating appoinment...',
                  success: <b>Appoinment created successfully!</b>,
                  error: <b>Failed to updating appoinment.</b>,
                }
            );

            // setSuccessMessage('Appointment created successfully!');
            // setLoading(false);
            history('/appoinment-history'); // Redirect after successful creation
            setErrors({});
        } catch (error) {
            if (error.response && error.response.data) {
                setErrors(error.response.data);
            }
        }
    };

    return (
        <div className="dashboard-container">
            <Sidebar /> {/* Sidebar component */}
            <div className="dashboard-main">
                <Header /> {/* Header component */}
                <div className="content-box create-doctor"> {/* Matching structure with patient page */}

                    {/* Doctor Creation Form */}
                    <div className="patient-details-container create-patient">

                        {/* Doctor Profile Header */}
                        <div className="patient-header appoinment-header">
                            <div className="back-icon-wrapper">
                                <Link to="/appoinment-history">
                                    <FontAwesomeIcon icon={faArrowLeft} className="back-icon" />
                                </Link>
                            </div>
                            <div className="center-content">
                                <h2 className="profile-name">Make Appoinment</h2>
                            </div>
                        </div>

                        <div className="patient-info-div">
                            <div className="patient-info appoinment-info">
                                <form onSubmit={handleSubmit} className="patient-info-form create-prescription">

                                    <div className="form-group row-double">
                                        <select
                                            name="speaciality"
                                            value={formData.speaciality}
                                            onChange={handleChange}
                                            required
                                        >
                                            <option value="">Select Speciality</option>
                                            {specialities.map((speciality) => (
                                                <option key={speciality.value} value={speciality.label}>
                                                    {speciality.label}
                                                </option>
                                            ))}
                                        </select>
                                        <label>Speciality *</label>
                                        {errors.speaciality && <span className="text-danger">{errors.speaciality}</span>}
                                    </div>
                                    <div className="form-group row-double">
                                      <select
                                        name="doctor"
                                        value={formData.doctor}
                                        onChange={handleDoctorChange}
                                        className="form-control"
                                        required
                                      >
                                        <option value="">Select Doctor's Name</option>
                                        {doctors.map((doctor) => (
                                          <option key={doctor.id} value={doctor.id}>
                                            {doctor.title} {doctor.full_name}
                                          </option>
                                        ))}
                                      </select>
                                      <label>Doctor *</label>
                                      {errors.doctor && <span className="text-danger">{errors.doctor}</span>}
                                    </div>
                                    {/* Date Picker with Flatpickr */}
                                    <div className="form-group">
                                        <input
                                            ref={datePickerRef}
                                            type="text"
                                            name="date"
                                            value={formData.date}
                                            placeholder="Select Date"
                                            className="form-control"
                                            required
                                            title="Please select a valid date."
                                        />
                                        <label>Date *</label>
                                        {errors.date && <span className="text-danger">{errors.date}</span>}
                                    </div>

                                    {/* Time Picker with Flatpickr */}
                                    <div className="form-group">
                                        <input
                                            ref={timePickerRef}
                                            type="text"
                                            name="time"
                                            value={formData.time}
                                            placeholder="Select Time"
                                            className="form-control"
                                            required
                                        />
                                        <label>Time *</label>
                                        {errors.time && <span className="text-danger">{errors.time}</span>}
                                    </div>

                                    {/* Patient info */}
                                    <div className="form-group">
                                        <input
                                            type="text"
                                            name="patient_number"
                                            value={formData.patient_number}
                                            placeholder="Enter Patient's Number e.g. 01xxx.."
                                            onChange={handleChange}
                                            className="form-control"
                                            pattern="[0-9]{11}"
                                            title="Please enter a valid 11-digit Bangladeshi mobile number starting with 01."
                                            required
                                        />
                                        <label>Patient Number *</label>
                                        {errors.patient_number && <span className="text-danger">{errors.patient_number}</span>}
                                        {patientNotFound && <span className="text-danger">New patient</span>} {/* Display if no patient found */}
                                    </div>

                                    {/* Patient Name with Datalist */}
                                     <div className="form-group">
                                         <input
                                             type="text"
                                             name="patient_name"
                                             list="patientNames" // Attach the datalist
                                             placeholder="Enter Patient's Name"
                                             value={formData.patient_name}
                                             onChange={handleChange}
                                             className="form-control"
                                             required
                                         />
                                         <label>Patient Name *</label>
                                         <datalist id="patientNames">
                                             {patients.map((patient) => (
                                                 <option key={patient.id} value={patient.name}>
                                                     {patient.name}
                                                 </option>
                                             ))}
                                         </datalist>
                                         {errors.patient_name && <span className="text-danger">{errors.patient_name}</span>}
                                     </div>

                                    {/* Submit Button */}
                                    <div className="form-group submit-button-container">
                                      <button type="submit" className="submit-btn" disabled={loading}>
                                        {loading ? 'Creating...' : '+ Make Appointment'}
                                      </button>
                                    </div>
                                    {successMessage && <div className="alert alert-success">{successMessage}</div>}
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="footer-content">
                    {process.env.REACT_APP_COPYRIGHT_TEXT || "© 2025, Powered by AmarLab"}
                </div>
            </div>
        </div>
    );
};

export default CreateAppointment;
